import React, { useEffect, useMemo } from 'react';
import { useCountdown } from '../../../hooks';

interface Props {
  duration: number; // Duration for timer in ms
  className?: string;
  onTimeElapsed?: () => any;
}

const Timer: React.FC<Props> = ({ children, className, duration, onTimeElapsed = () => {} }) => {
  const { timeFormats, isTimeElapsed, updateCountdown, getCountdownMs } = useCountdown({ duration });

  const timerCountdown = useMemo((): string => {
    const timesOnArray: number[] = Object.values(timeFormats);

    return timesOnArray
      .map((num: number) => num.toString())
      .map((numStr: string) => (numStr.length === 1 ? `0${numStr}` : numStr))
      .join(':');
  }, [timeFormats]);

  useEffect(() => {
    if (isTimeElapsed) {
      onTimeElapsed();
    }
  }, [isTimeElapsed]);

  // Due to a browser inactive tab throttling mechanism we need to adjust countdown in case of tab was inactive
  useEffect(() => {
    let hiddenDurationStart = 0;
    let countdownStart = 0;

    function handleVisibilityChange() {
      if (document.hidden) {
        hiddenDurationStart = Date.now();
        countdownStart = getCountdownMs();
      } else {
        const hiddenDiff = Date.now() - hiddenDurationStart;
        const countdownDiff = countdownStart - getCountdownMs();
        const adjustedValue = hiddenDiff - countdownDiff;

        if (adjustedValue > 0) {
          updateCountdown(adjustedValue);
        }

        hiddenDurationStart = 0;
        countdownStart = 0;
      }
    }

    addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [getCountdownMs, updateCountdown]);

  return (
    <span className={className}>
      {timerCountdown} {timerCountdown && children}
    </span>
  );
};

export default Timer;
